import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Clock, Coffee } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useAppSelector } from '@/Redux/store';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, type CurrentAttendance } from './lib';

/**
 * The time clock. A station screen — opened once at the start of a shift and
 * left running, the same reasoning the till and the pass earn their own rows
 * for — gated on `attendance.punch`, which every floor role holds (a waiter
 * who cannot clock in is a waiter who is not paid).
 *
 * Location is never requested here. ADR-0008 §6: capture is `OFF` by default
 * and this screen does not ask the browser for a coordinate on its own — a
 * venue that has turned `locationCaptureMode: ON_PUNCH` on integrates it
 * through the terminal/device path (`deviceId`), not through the web client
 * asking a browser permission dialog for staff GPS.
 */
export default function TimeClock() {
  const { t, i18n } = useTranslation();
  const branches = useBranches();
  const activeBranchId = useAppSelector((s) => s.ui.activeBranchId);

  const [pickedBranch, setPickedBranch] = useState('');
  const branchId = pickedBranch || activeBranchId || branches[0]?.id || '';
  const [current, setCurrent] = useState<CurrentAttendance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<CurrentAttendance>('/attendance/me')
      .then(({ data }) => setCurrent(data))
      .catch((e) => setError(apiError(e, t('workforce.timeClock.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const punch = async (path: string, body: Record<string, unknown> = {}) => {
    setBusy(true);
    try {
      await api.post(`/attendance/${path}`, body);
      load();
    } catch (e) {
      toast.error(apiError(e, t('workforce.timeClock.punchFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!current) return null;

  return (
    <div className="mx-auto max-w-md space-y-4">
      <Card>
        <CardContent className="space-y-4 p-6 text-center">
          <Icon
            as={Clock}
            size="xl"
            className={current.clockedIn ? 'mx-auto text-green-600 dark:text-green-400' : 'mx-auto text-ink-muted'}
          />
          <div>
            <p className="text-lg font-semibold">
              {t(current.clockedIn ? 'workforce.timeClock.clockedIn' : 'workforce.timeClock.clockedOut')}
            </p>
            {current.entry && (
              <p className="text-sm text-ink-muted">
                {t('workforce.timeClock.since', {
                  date: formatDateTime(current.entry.clockInAt, i18n.language),
                })}
              </p>
            )}
            {current.onBreak && (
              <Badge tone="warning" className="mt-2">
                {t('workforce.timeClock.onBreak')}
              </Badge>
            )}
          </div>

          {!current.clockedIn && (
            <Field label={t('workforce.timeClock.branch')}>
              <Select value={branchId} onChange={(e) => setPickedBranch(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2">
            {!current.clockedIn ? (
              <Button
                className="col-span-2"
                size="lg"
                disabled={busy || !branchId}
                onClick={() => void punch('clock-in', { branchId })}
              >
                {t('workforce.timeClock.clockIn')}
              </Button>
            ) : (
              <>
                {!current.onBreak ? (
                  <Button variant="outline" disabled={busy} onClick={() => void punch('break/start')}>
                    <Icon as={Coffee} size="sm" className="me-1.5" />
                    {t('workforce.timeClock.startBreak')}
                  </Button>
                ) : (
                  <Button variant="outline" disabled={busy} onClick={() => void punch('break/end')}>
                    <Icon as={Coffee} size="sm" className="me-1.5" />
                    {t('workforce.timeClock.endBreak')}
                  </Button>
                )}
                <Button variant="danger" disabled={busy} onClick={() => void punch('clock-out')}>
                  {t('workforce.timeClock.clockOut')}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
